import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
  useCallback,
} from 'react';
import { authApi, setzeAbmeldeBehandlung } from '../services/api';
import { authStore, Benutzer, Mandant, Sitzung } from '../services/authStore';

/**
 * Zustand der Anmeldung.
 *
 * 'laedt' ist ein eigener Zustand und nicht bloss "noch nicht angemeldet":
 * beim Seitenaufbau ist das Access-Token immer weg (es liegt nur im
 * Modulspeicher), und das Refresh-Cookie wird erst gegen den Server
 * geprueft. Ohne diesen Zustand wuerde jedes Neuladen kurz die Anmeldemaske
 * zeigen, obwohl die Sitzung gilt.
 */
export type AnmeldeZustand = 'laedt' | 'anonym' | 'mfa_ausstehend' | 'angemeldet';

export interface MfaAusstehend {
  mfaToken: string;
  zielEmailMaskiert: string;
}

interface AuthWert {
  status: AnmeldeZustand;
  benutzer: Benutzer | null;
  mandant: Mandant | null;
  berechtigungen: ReadonlySet<string>;
  mfa: MfaAusstehend | null;
  anmelden: (email: string, passwort: string) => Promise<void>;
  mfaBestaetigen: (
    code: string,
    geraetVertrauen?: boolean,
    geraeteName?: string
  ) => Promise<void>;
  mfaErneutSenden: () => Promise<void>;
  abmelden: (alleGeraete?: boolean) => Promise<void>;
  neuLaden: () => Promise<void>;
  hatRecht: (schluessel: string) => boolean;
}

const AuthKontext = createContext<AuthWert | null>(null);

export function useAuth(): AuthWert {
  const wert = useContext(AuthKontext);
  if (!wert) throw new Error('useAuth benoetigt einen AuthProvider im Baum.');
  return wert;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AnmeldeZustand>('laedt');
  const [sitzung, setSitzung] = useState<Sitzung | null>(null);
  const [mfa, setMfa] = useState<MfaAusstehend | null>(null);

  const uebernehmen = useCallback((antwort: Sitzung) => {
    authStore.setzen(antwort);
    setSitzung(antwort);
    setMfa(null);
    setStatus('angemeldet');
  }, []);

  const abmeldenLokal = useCallback(() => {
    authStore.leeren();
    setSitzung(null);
    setMfa(null);
    setStatus('anonym');
  }, []);

  // Der Interceptor kennt den React-Baum nicht. Ueber diesen Rueckruf sagt
  // er Bescheid, wenn eine Erneuerung endgueltig gescheitert ist -- sonst
  // zeigte die Oberflaeche weiter einen angemeldeten Benutzer, waehrend jede
  // Abfrage 401 liefert.
  useEffect(() => {
    setzeAbmeldeBehandlung(abmeldenLokal);
  }, [abmeldenLokal]);

  useEffect(() => authStore.beobachten(setSitzung), []);

  // Beim Start einmal still erneuern. Gelingt es, war das Refresh-Cookie
  // gueltig und der Benutzer bleibt angemeldet.
  useEffect(() => {
    let abgebrochen = false;

    (async () => {
      try {
        // Die Erneuerung liefert Token, Benutzer und Mandant in einem
        // Zug. Ein zusaetzlicher Profilabruf waere eine Anfrage mehr auf
        // dem Weg zum ersten Bild.
        const erneuert = (await authApi.erneuern()) as unknown as Sitzung;
        if (abgebrochen) return;
        uebernehmen(erneuert);
      } catch {
        // Kein gueltiges Cookie -- das ist der Normalfall beim ersten
        // Besuch und kein Fehler, der gemeldet werden muesste.
        if (!abgebrochen) abmeldenLokal();
      }
    })();

    return () => {
      abgebrochen = true;
    };
  }, [uebernehmen, abmeldenLokal]);

  const anmelden = useCallback(
    async (email: string, passwort: string) => {
      const antwort = await authApi.anmelden({ email, passwort });

      if (antwort.mfa_erforderlich && antwort.mfa_token) {
        setMfa({
          mfaToken: antwort.mfa_token,
          zielEmailMaskiert: antwort.ziel_email_maskiert ?? '',
        });
        setStatus('mfa_ausstehend');
        return;
      }

      uebernehmen(antwort as unknown as Sitzung);
    },
    [uebernehmen]
  );

  const mfaBestaetigen = useCallback(
    async (code: string, geraetVertrauen = false, geraeteName?: string) => {
      if (!mfa) throw new Error('Es steht keine Bestaetigung aus.');
      const antwort = await authApi.mfaBestaetigen({
        mfa_token: mfa.mfaToken,
        code,
        geraet_vertrauen: geraetVertrauen,
        geraete_name: geraeteName,
      });
      uebernehmen(antwort as Sitzung);
    },
    [mfa, uebernehmen]
  );

  const mfaErneutSenden = useCallback(async () => {
    if (!mfa) return;
    await authApi.mfaErneutSenden({ mfa_token: mfa.mfaToken });
  }, [mfa]);

  const abmelden = useCallback(
    async (alleGeraete = false) => {
      try {
        await authApi.abmelden(alleGeraete);
      } finally {
        // Auch wenn der Server nicht erreichbar war: lokal ist die Sitzung
        // beendet. Alles andere waere fuer den Benutzer eine Luege.
        abmeldenLokal();
      }
    },
    [abmeldenLokal]
  );

  const neuLaden = useCallback(async () => {
    const profil = await authApi.ich();
    setSitzung((alt) => (alt ? { ...alt, ...profil } : alt));
  }, []);

  const berechtigungen = useMemo(
    () => new Set(sitzung?.berechtigungen ?? []),
    [sitzung]
  );

  const hatRecht = useCallback(
    (schluessel: string) => {
      // Bis die Berechtigungsmatrix steht (Phase 0b), liefert der Server
      // keine Rechteliste. Solange gilt: angemeldet ist berechtigt -- was
      // dem Stand vor Phase 0 entspricht, in dem es ueberhaupt keine
      // Anmeldung gab.
      if (sitzung?.benutzer?.ist_plattform_admin) return true;
      if (berechtigungen.size === 0) return true;
      return berechtigungen.has(schluessel);
    },
    [berechtigungen, sitzung]
  );

  const wert = useMemo<AuthWert>(
    () => ({
      status,
      benutzer: sitzung?.benutzer ?? null,
      mandant: sitzung?.mandant ?? null,
      berechtigungen,
      mfa,
      anmelden,
      mfaBestaetigen,
      mfaErneutSenden,
      abmelden,
      neuLaden,
      hatRecht,
    }),
    [
      status,
      sitzung,
      berechtigungen,
      mfa,
      anmelden,
      mfaBestaetigen,
      mfaErneutSenden,
      abmelden,
      neuLaden,
      hatRecht,
    ]
  );

  return <AuthKontext.Provider value={wert}>{children}</AuthKontext.Provider>;
}
